import {
  assertPointChange,
  baselineOriginalFor,
  DomainError,
  LIMIT_SOURCES,
  parseMeasurementPointInput,
} from '@felix/domain';
import { z } from 'zod';
import type { LimitSource, MeasurementPoint, MeasurementPointInput } from './condition-types.js';

/**
 * Ölçüm noktası ve limit formu (React Hook Form + Zod; plan M7 Task 21a; K18, K19): kurallar
 * domain'in `measurementPointInputSchema`'sından ve `assertPointChange`'inden gelir; sunucu aynı
 * kurallarla yeniden denetler. Hata iletisi domain kodudur (`pms.errors.<KOD>` / `pms.fieldErrors`).
 */

type NumberField = number | '';

export interface PointFormValues {
  equipmentId: string;
  jobDefinitionId: string | null;
  name: string;
  unit: string;
  nominal: NumberField;
  alertLow: NumberField;
  alertHigh: NumberField;
  dangerLow: NumberField;
  dangerHigh: NumberField;
  lowInclusive: boolean;
  highInclusive: boolean;
  limitSource: LimitSource;
  provider: string;
  baselineValue: NumberField;
  baselineTakenOn: string;
  autoDefect: boolean;
  autoDefectOffReason: string;
  /** Limit, kaynak ya da baseline değişikliğinin gerekçesi [UR Z27 2.2.4]. */
  changeReason: string;
}

export function emptyPointForm(
  equipmentId: string,
  jobDefinitionId: string | null,
): PointFormValues {
  return {
    equipmentId,
    jobDefinitionId,
    name: '',
    unit: '',
    nominal: '',
    alertLow: '',
    alertHigh: '',
    dangerLow: '',
    dangerHigh: '',
    lowInclusive: true,
    highInclusive: true,
    limitSource: 'oem',
    provider: '',
    baselineValue: '',
    baselineTakenOn: '',
    autoDefect: true,
    autoDefectOffReason: '',
    changeReason: '',
  };
}

const field = (value: number | null): NumberField => value ?? '';

export function pointFormOf(point: MeasurementPoint): PointFormValues {
  return {
    equipmentId: point.equipmentId,
    jobDefinitionId: point.jobDefinitionId,
    name: point.name,
    unit: point.unit,
    nominal: field(point.nominal),
    alertLow: field(point.alertLow),
    alertHigh: field(point.alertHigh),
    dangerLow: field(point.dangerLow),
    dangerHigh: field(point.dangerHigh),
    lowInclusive: point.lowInclusive,
    highInclusive: point.highInclusive,
    limitSource: (LIMIT_SOURCES as readonly string[]).includes(point.limitSource)
      ? (point.limitSource as LimitSource)
      : 'oem',
    provider: point.provider ?? '',
    baselineValue: field(point.baselineValue),
    baselineTakenOn: point.baselineTakenOn ?? '',
    autoDefect: point.autoDefect,
    autoDefectOffReason: point.autoDefectOffReason ?? '',
    changeReason: '',
  };
}

const blank = (value: string) => (value.trim() === '' ? null : value.trim());
const num = (value: NumberField) => (value === '' ? null : value);

/** Formdan sunucunun beklediği girdi. */
export function toPointInput(values: PointFormValues): MeasurementPointInput {
  return {
    equipmentId: values.equipmentId,
    jobDefinitionId: values.jobDefinitionId,
    name: values.name,
    unit: values.unit,
    nominal: num(values.nominal),
    alertLow: num(values.alertLow),
    alertHigh: num(values.alertHigh),
    dangerLow: num(values.dangerLow),
    dangerHigh: num(values.dangerHigh),
    lowInclusive: values.lowInclusive,
    highInclusive: values.highInclusive,
    limitSource: values.limitSource,
    provider: blank(values.provider),
    baselineValue: num(values.baselineValue),
    baselineTakenOn: blank(values.baselineTakenOn),
    autoDefect: values.autoDefect,
    autoDefectOffReason: values.autoDefect ? null : blank(values.autoDefectOffReason),
    changeReason: blank(values.changeReason),
    sortOrder: 0,
    active: true,
  };
}

/** Düzenlemenin gönderilecek alanları: yalnız değişenler ve gerekçe. */
export function pointPatchOf(
  existing: MeasurementPoint,
  values: PointFormValues,
): Partial<MeasurementPointInput> {
  const input = toPointInput(values);
  const patch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (key === 'equipmentId' || key === 'sortOrder' || key === 'active') continue;
    const before = (existing as unknown as Record<string, unknown>)[key] ?? null;
    if (key === 'changeReason' ? value !== null : value !== before) patch[key] = value;
  }
  return patch as Partial<MeasurementPointInput>;
}

/** Domain alan yolu → formun alanı (`limits` ilk limit kutusuna). */
function formPath(path: string): string[] {
  if (path === 'limits' || path === '') return ['alertLow'];
  return path.split('.');
}

const numberField = z.union([z.number(), z.literal('')]);

/**
 * Formun şeması. `today`: geminin yerel günü (baseline tarihi gelecekte olamaz). `existing`:
 * düzenlenen nokta; limit, kaynak ya da baseline değişince gerekçe istenir [UR Z27 2.2.4].
 */
export function pointFormSchema(options: { today: string; existing?: MeasurementPoint }) {
  return z
    .object({
      equipmentId: z.string(),
      jobDefinitionId: z.string().nullable(),
      name: z.string(),
      unit: z.string(),
      nominal: numberField,
      alertLow: numberField,
      alertHigh: numberField,
      dangerLow: numberField,
      dangerHigh: numberField,
      lowInclusive: z.boolean(),
      highInclusive: z.boolean(),
      limitSource: z.enum(LIMIT_SOURCES),
      provider: z.string(),
      baselineValue: numberField,
      baselineTakenOn: z.string(),
      autoDefect: z.boolean(),
      autoDefectOffReason: z.string(),
      changeReason: z.string(),
    })
    .superRefine((values, ctx) => {
      const input = toPointInput(values as PointFormValues);
      const parsed = parseMeasurementPointInput(input, { today: options.today });
      if (!parsed.ok) {
        for (const issue of parsed.issues) {
          ctx.addIssue({ code: 'custom', message: issue.code, path: formPath(issue.path) });
        }
        return;
      }
      const existing = options.existing;
      if (!existing) return;
      const snapshot = (p: MeasurementPointInput | MeasurementPoint) => ({
        equipmentId: p.equipmentId,
        unit: p.unit,
        limitSource: p.limitSource as LimitSource,
        alertLow: p.alertLow,
        alertHigh: p.alertHigh,
        dangerLow: p.dangerLow,
        dangerHigh: p.dangerHigh,
        lowInclusive: p.lowInclusive,
        highInclusive: p.highInclusive,
        baselineValue: p.baselineValue,
      });
      try {
        assertPointChange(
          {
            ...snapshot(existing),
            baselineOriginal: existing.baselineOriginal,
            changeReason: existing.changeReason,
          },
          {
            ...snapshot(input),
            baselineOriginal: baselineOriginalFor(existing.baselineOriginal, input.baselineValue),
            changeReason: input.changeReason,
          },
          { hasMeasurements: existing.last !== null },
        );
      } catch (error) {
        if (!(error instanceof DomainError)) throw error;
        const path = error.code === 'UNIT_LOCKED' ? 'unit' : 'changeReason';
        ctx.addIssue({ code: 'custom', message: error.code, path: [path] });
      }
    });
}
